import type { FC } from "react";
import FormGroup from "@mui/material/FormGroup";
import { Checkbox } from "@privacy/ui";
import { useMutation } from "@tanstack/react-query";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import type { InputProps } from "../../DynamicInput";
import type { Option as OptionType } from "../../types";
import { useDynamicFormState } from "../../DynamicFormProvider";
import { ErrorMessage } from "../../inlineForm/ErrorMessage";
import { isAnswerRequestEmpty } from "../../shared/isAnswerRequestEmpty";
import { Option } from "./Option";

type CheckboxGroupProps = InputProps<string[]> & { options: OptionType[] };
export const CheckboxGroup: FC<CheckboxGroupProps> = ({ questionCard, readonly, options, onSave }) => {
  const { control, getUpdatedSelectedValues, isOptionChecked } = useCheckboxGroup(questionCard.id);
  const { mandatoryQuestionErrorMessage } = useDynamicFormState();

  const saveMutation = useMutation({
    mutationFn: async (values: string[]) => onSave(values),
  });

  const value = useWatch({ name: questionCard.id });

  return (
    <FormGroup>
      {options.map(option => (
        <Controller
          key={option.value}
          name={questionCard.id}
          control={control}
          render={({ field: { onChange } }) => (
            <Option
              disabled={readonly}
              option={option}
              onClick={() => {
                const newValues = getUpdatedSelectedValues(option.value);
                onChange(newValues);
                saveMutation.mutate(newValues);
              }}
              checked={isOptionChecked(option.value)}
              Control={Checkbox}
            />
          )}
        />
      ))}
      {!saveMutation.isPending && saveMutation.isError && <ErrorMessage />}
      {mandatoryQuestionErrorMessage && questionCard.mandatory && isAnswerRequestEmpty(value) && (
        <ErrorMessage variant="warning" errorMessage={mandatoryQuestionErrorMessage} />
      )}
    </FormGroup>
  );
};

const useCheckboxGroup = (name: string) => {
  const { control } = useFormContext();

  const selectedValues: string[] = useWatch({ name, control }) ?? [];

  const getUpdatedSelectedValues = (optionValue: string) => {
    return selectedValues.includes(optionValue)
      ? selectedValues.filter(s => s !== optionValue)
      : [optionValue, ...selectedValues];
  };

  const isOptionChecked = (optionValue: string) => selectedValues?.some(v => v === optionValue);

  return { isOptionChecked, getUpdatedSelectedValues, control };
};
